import React from 'react';

export const CONTACT_EMAIL = 'customersupport@appancestor.com';

const Mail = () => <a className="u-link" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
const GooglePrivacy = ({ children }) => <a className="u-link" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">{children}</a>;
const GoogleAds = ({ children }) => <a className="u-link" href="https://adssettings.google.com/" target="_blank" rel="noreferrer">{children}</a>;

/**
 * Both language editions of the LianWan policy. Each edition carries its own
 * chrome labels plus an ordered list of blocks, so the page renders one way and
 * only the content differs. Same shape as the EyesDwell policy.
 * @author Nelson
 */
const content = {
    en: {
        htmlLang: 'en',
        docTitle: 'LianWan – Privacy Policy',
        title: 'LianWan – Privacy Policy',
        back: 'Go Back',
        effectiveLabel: 'Effective',
        updatedLabel: 'Last Updated',
        effective: 'September 7, 2026',
        updated: 'September 7, 2026',
        developerLabel: 'Developer',
        blocks: [
            ['p', <>LianWan (联玩) is a party-game app for iPhone, developed by <strong>YUAN LAB ENTERPRISE</strong>, Malaysia. One phone is passed around a table; there is no account, no login and no server of our own.</>],
            ['p', <>If you have questions about this Privacy Policy, contact us at <Mail />.</>],

            ['h3', '1. What We Do Not Collect'],
            ['p', <>LianWan does <b>not</b> ask for or collect your name, email address, phone number, date of birth, gender, location, contacts, photos or any other personal information. There is no account system and no login.</>],
            ['p', <>LianWan itself does not send anything to a server and does not use third-party analytics or crash-reporting tools. The only thing it checks is whether the device is online, because ads need a connection.</>],

            ['h3', '2. What Is Stored on Your Device'],
            ['p', <>The app keeps the following on your device only:</>],
            ['ul', [
                'Music and sound-effect volume and mute settings',
                'A record of which purchases you own, so the app opens in the right state without waiting for the App Store',
                'Ad counters and the time of the last ad, used only to space ads out',
                'The daily play count for the three games in the Quick Wits Pack',
                'Which “new” badges you have already tapped'
            ]],
            ['p', <>Deleting the app removes these, with one exception: the daily play count and the “new” badge record are kept in the iPhone’s keychain, which iOS preserves across a reinstall. This is deliberate, so that reinstalling does not reset the daily limit. Neither contains anything that identifies you.</>],
            ['p', <><strong>Google’s advertising and consent SDKs may keep their own local storage. That storage is managed by Google and is separate from the list above.</strong></>],

            ['h3', '3. Google AdMob'],
            ['p', <>LianWan shows banner and rewarded advertisements through <strong>Google AdMob</strong>. A rewarded ad plays only after you tap a button that says so. AdMob may collect your IP address, device identifiers, and advertising interaction data for purposes such as serving ads, measuring them, limiting frequency and preventing fraud.</>],
            ['p', <>LianWan requests non-personalized ads only.</>],
            ['p', <>In regions where consent is legally required, such as the European Union, the United Kingdom and Switzerland, the app shows a consent dialog before any ad is requested. Users in those regions can change their choice at any time in <strong>Settings → Privacy Settings</strong> inside the app.</>],
            ['p', <>For more information, see Google’s <GooglePrivacy>Privacy Policy</GooglePrivacy> and <GoogleAds>Ads Settings</GoogleAds>.</>],

            ['h4', 'Tracking and Advertising ID'],
            ['p', <>LianWan does not request Apple’s App Tracking Transparency permission.</>],
            ['p', <>LianWan does not perform cross-app or cross-website tracking and does not obtain or use Apple’s IDFA advertising identifier.</>],

            ['h3', '4. In-App Purchases'],
            ['p', <>LianWan offers one-time purchases: the <strong>Quick Wits Pack</strong>, <strong>Remove Ads</strong>, and a bundle of both. None is a subscription and none requires an account.</>],
            ['p', <>Payment is handled entirely by Apple. YUAN LAB ENTERPRISE never sees your payment information. Purchases are tied to your Apple ID and can be restored on a new device with “Restore Purchases”.</>],
            ['p', <>After Remove Ads is purchased, the app no longer loads the advertising SDK.</>],

            ['h3', '5. Apple-Shared Diagnostics and Usage Data'],
            ['p', <>If you choose in iOS Settings to share analytics with app developers, Apple may provide us with crash reports and usage statistics that do not identify you.</>],

            ['h3', '6. Children'],
            ['p', <>LianWan is not directed to children under 13.</>],

            ['h3', '7. Your Choices and Rights'],
            ['p', <>Because LianWan has no accounts, there is no account-based data portal, and we hold no personal data about you.</>],
            ['p', <>For Google AdMob’s collection of device identifiers, the legal basis in regions where consent is required is your consent. In other regions, the legal basis is determined under applicable local law.</>],
            ['p', <>If you are in the European Union or the United Kingdom, you have the right to lodge a complaint with the data protection supervisory authority in the country where you are located.</>],
            ['p', <>Advertising data processed by Google may be transferred to and stored in countries outside Malaysia, including the United States. These transfers are made using safeguards provided by Google.</>],
            ['p', <>Google handles the retention period for advertising data according to Google’s own data retention policies.</>],
            ['p', <>If you are a California resident, sharing device identifiers for advertising may constitute “sharing” under California law. Where applicable, you can withdraw advertising consent in the in-app privacy settings or contact us by email to make a request.</>],
            ['p', <>Depending on your location, you can:</>],
            ['ul', [
                'Withdraw advertising consent in Settings → Privacy Settings when that option is provided.',
                'Delete the app to remove its locally stored preferences (see section 2 for the two keychain items).',
                <>Contact us by email at <Mail /> with questions or requests concerning your data.</>
            ]],

            ['h3', '8. Changes to This Policy'],
            ['p', <>If this Privacy Policy changes, we will update this page and the <strong>Last Updated</strong> date above.</>]
        ]
    },

    zh: {
        htmlLang: 'zh-Hans',
        docTitle: '联玩 – 隐私政策',
        title: '联玩 – 隐私政策',
        back: '返回',
        effectiveLabel: '生效日期',
        updatedLabel: '最后更新',
        effective: '2026年9月7日',
        updated: '2026年9月7日',
        developerLabel: '开发者',
        blocks: [
            ['p', <>联玩（LianWan）是一款 iPhone 聚会游戏 App，由马来西亚 <strong>YUAN LAB ENTERPRISE</strong> 开发。一台手机在桌上传着玩；没有账号、没有登录，也没有我们自己的服务器。</>],
            ['p', <>如对本隐私政策有任何疑问，请通过电子邮件联系：<Mail />。</>],

            ['h3', '1. 我们不收集什么'],
            ['p', <>联玩<b>不会</b>要求或收集你的姓名、电子邮件、电话号码、出生日期、性别、位置、通讯录、照片或任何其他个人信息。没有账号系统，也没有登录。</>],
            ['p', <>联玩本身不会向任何服务器发送资料，也没有使用第三方分析或崩溃报告工具。App 唯一会检查的是设备有没有连网，因为广告需要网络。</>],

            ['h3', '2. 保存在你设备上的内容'],
            ['p', <>App 只在你的设备上保存以下内容：</>],
            ['ul', [
                '音乐与音效的音量和静音设置',
                '你拥有哪些购买项目的记录，让 App 打开时不必等待 App Store 回应',
                '广告计数与上一次看广告的时间，只用于控制广告间隔',
                '急智包三款游戏的每日次数',
                '哪些「新」贴纸你已经点过'
            ]],
            ['p', <>删除 App 会清除这些内容，只有一个例外：每日次数和「新」贴纸记录保存在 iPhone 的钥匙串中，iOS 会在重装后保留它们。这是刻意的设计，为了让重装不会重置每日次数。两者都不包含任何能识别你身份的内容。</>],
            ['p', <><strong>Google 的广告与同意 SDK 可能会自行进行本机储存。这部分由 Google 管理，与上述内容分开。</strong></>],

            ['h3', '3. Google AdMob'],
            ['p', <>联玩通过 <strong>Google AdMob</strong> 显示横幅广告和奖励广告。奖励广告只会在你按下写明会播放广告的按钮之后才播放。AdMob 可能会收集你的 IP 地址、设备识别码和广告互动资料，用于播放广告、衡量广告、控制频次和防止欺诈等目的。</>],
            ['p', <>联玩只请求非个性化广告。</>],
            ['p', <>在法律要求取得同意的地区（例如欧盟、英国、瑞士），App 会在请求任何广告之前显示同意窗口。这些地区的用户可以随时在 App 内的<strong>「设置 → 隐私设置」</strong>改变选择。</>],
            ['p', <>更多信息请参阅 Google 的<GooglePrivacy>隐私政策</GooglePrivacy>与<GoogleAds>广告设置</GoogleAds>。</>],

            ['h4', '追踪与广告识别码'],
            ['p', <>联玩不会请求 Apple 的 App Tracking Transparency 授权。</>],
            ['p', <>联玩不进行跨 App 或跨网站追踪，也不会取得或使用 Apple 的 IDFA 广告识别码。</>],

            ['h3', '4. App 内购买'],
            ['p', <>联玩提供一次性买断的购买项目：<strong>急智包</strong>、<strong>去除广告</strong>，以及两者的组合。都不是订阅，也不需要账号。</>],
            ['p', <>付款完全由 Apple 处理，YUAN LAB ENTERPRISE 不会看到你的付款资料。购买跟着你的 Apple ID 走，换手机后可用「恢复购买」拿回。</>],
            ['p', <>买断去除广告后，App 不再载入广告 SDK。</>],

            ['h3', '5. Apple 分享的崩溃与使用统计'],
            ['p', <>如果你在 iOS 设置中选择与 App 开发者分享分析资料，Apple 可能会向我们提供不含你身份的崩溃报告与使用统计。</>],

            ['h3', '6. 儿童'],
            ['p', <>联玩不面向 13 岁以下儿童。</>],

            ['h3', '7. 你的选择与权利'],
            ['p', <>由于联玩没有账号，因此没有以账号为基础的资料管理入口，我们也不持有任何关于你的个人资料。</>],
            ['p', <>对于 Google AdMob 收集设备识别码，在需要取得同意的地区，法律依据是你的同意。在其他地区，法律依据依当地适用法律决定。</>],
            ['p', <>如果你位于欧盟或英国，你有向所在国家的资料保护监督机关提出申诉的权利。</>],
            ['p', <>广告资料由 Google 处理，可能被传输并存放于马来西亚以外的国家，包括美国。该传输依 Google 提供的保障措施进行。</>],
            ['p', <>Google 就广告资料的保留期限，依 Google 自己的资料保留政策处理。</>],
            ['p', <>如果你是加州居民，为播放广告而分享设备识别码，在加州法下可能构成「分享」。在适用地区，你可以在 App 内的隐私设置撤回广告同意，或来信提出请求。</>],
            ['p', <>根据你所在的地区，你可以：</>],
            ['ul', [
                '在适用地区通过「设置 → 隐私设置」撤回广告同意。',
                '删除 App，以清除本机保存的偏好（第 2 节提到的两项钥匙串记录除外）。',
                <>如对个人资料有疑问或提出相关请求，请联系：<Mail />。</>
            ]],

            ['h3', '8. 隐私政策的变更'],
            ['p', <>如本隐私政策发生变更，我们会更新本页面，并更新上方的<strong>最后更新日期</strong>。</>]
        ]
    }
};

export default content;
